/**
 * Carrier Adjuster API Service (PR4, 2026-11-04)
 *
 * TypeScript client wrapping the carrier REST endpoints.
 * Uses the existing `api` utility from utils/api.ts for auth + error handling.
 */

import { api } from '../utils/api';

const BASE = '/api/v1/carrier';

// ---------------------------------------------------------------------------
// TYPES
// ---------------------------------------------------------------------------

export interface CarrierArtifact {
  artifact_id: string | null;
  type: string | null;
  file_key: string | null;
  sha256: string | null;
}

export interface CarrierDeliveryListItem {
  delivery_id: string | null;
  case_id: string | null;
  delivered_at: string | null;
  acknowledged_at: string | null;
  artifacts: CarrierArtifact[];
}

export interface CarrierDeliveryListResponse {
  deliveries: CarrierDeliveryListItem[];
}

export interface CarrierArtifactDetail extends CarrierArtifact {
  figures_as_rendered?: Record<string, unknown> | null;
}

export interface CarrierDeliveryResponse {
  delivery_id: string | null;
  case_id: string | null;
  delivered_at: string | null;
  delivered_by: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  artifacts: CarrierArtifactDetail[];
}

export interface CarrierAckResponse {
  delivery_id: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
}

export interface CarrierInboundResponse {
  inbound_id: string;
  delivery_id: string;
  s3_key: string;
  sha256: string;
  size_bytes: number;
  mime_type: string;
  filename: string;
}

export interface CarrierRfiBody {
  message: string;
}

export interface CarrierRfiResponse {
  delivery_id: string;
  message_count: number;
}

export interface CarrierThreadMessage {
  id?: string;
  sender?: string;
  text?: string;
  created_at?: string;
  kind?: string;
}

export interface CarrierThreadResponse {
  delivery_id: string;
  messages: CarrierThreadMessage[];
}

export interface CarrierPositionLine {
  label: string;
  figure_key?: string | null;
  amount?: number | null;
  status: string;
  reason: string;
}

export interface CarrierPositionBody {
  kind: string;
  amount?: number | null;
  lines: CarrierPositionLine[];
}

export interface CarrierPositionResponse {
  position_id: string;
  delivery_id: string;
  case_id: string | null;
  kind: string;
  source: string;
  status: string;
  amount: number | null;
  lines: CarrierPositionLine[];
  created_at: string;
}

// ---------------------------------------------------------------------------
// API FUNCTIONS
// ---------------------------------------------------------------------------

/** List all frozen deliveries for this carrier org. */
export async function listDeliveries(): Promise<CarrierDeliveryListResponse> {
  return api.get<CarrierDeliveryListResponse>(`${BASE}/deliveries`);
}

/** Get a single frozen delivery, including its figures_as_rendered. */
export async function getDelivery(deliveryId: string): Promise<CarrierDeliveryResponse> {
  return api.get<CarrierDeliveryResponse>(`${BASE}/deliveries/${deliveryId}`);
}

/** Fetch artifact bytes with Authorization (bare <a href> would 401). */
export async function downloadArtifact(
  deliveryId: string,
  artifactIndex: number,
): Promise<Blob> {
  return api.get<Blob>(`${BASE}/deliveries/${deliveryId}/artifacts/${artifactIndex}`);
}

/** Acknowledge receipt of a delivery. */
export async function ack(deliveryId: string): Promise<CarrierAckResponse> {
  return api.post<CarrierAckResponse>(`${BASE}/deliveries/${deliveryId}/ack`, {});
}

/** Carrier uploads an inbound file for a delivery. */
export async function postInbound(
  deliveryId: string,
  file: File,
): Promise<CarrierInboundResponse> {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<CarrierInboundResponse>(`${BASE}/deliveries/${deliveryId}/inbound`, formData);
}

/** Carrier posts an RFI message on a delivery thread. */
export async function postRfi(
  deliveryId: string,
  body: CarrierRfiBody,
): Promise<CarrierRfiResponse> {
  return api.post<CarrierRfiResponse>(`${BASE}/deliveries/${deliveryId}/rfi`, body);
}

/** Get the RFI thread for a delivery. */
export async function getThread(deliveryId: string): Promise<CarrierThreadResponse> {
  return api.get<CarrierThreadResponse>(`${BASE}/deliveries/${deliveryId}/thread`);
}

/** Carrier submits a position (accept/questioned/denied lines). */
export async function postPosition(
  deliveryId: string,
  body: CarrierPositionBody,
): Promise<CarrierPositionResponse> {
  return api.post<CarrierPositionResponse>(`${BASE}/deliveries/${deliveryId}/position`, body);
}
